import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SearchX } from "lucide-react";

import {
  DataTable,
  type DataTableColumn,
  EmptyState,
  H2,
  Mono,
  PageHeader,
  SearchInput,
  Stat,
  Text,
  useDebouncedValue,
} from "@lagoon/ui";

import { api, type Health, type SearchOwner, type SearchSite } from "../api.js";
import { AccountStateBadge, SiteStateBadge } from "../components/badges.js";
import { formatCount } from "../format.js";
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from "../search.js";

/**
 * The overview, and one box over every tenant.
 *
 * The console's entry point is a search rather than a list: an operator
 * arrives here because someone named a site or an address, not to browse. The
 * counts sit above it because they are the reason somebody opens the console
 * without a name in mind: a queue that is not empty, or a suspension nobody
 * mentioned.
 */
export function SearchScreen({ health }: { health: Health | undefined }): React.JSX.Element {
  const [term, setTerm] = useState("");
  const trimmed = term.trim();
  // The box stays instant; only the query lags. Every character used to be a
  // cross-tenant ILIKE (LGN-125, audit M13).
  const settled = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);

  const results = useQuery({
    queryKey: ["search", settled],
    queryFn: () => api.search(settled),
    enabled: settled.length >= MIN_SEARCH_LENGTH,
  });

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Tech Ops"
        title="Operations"
        description="Every tenant on this stack, and what the platform is doing right now."
      />

      <Counts health={health} />

      <div className="lg:flex lg:flex-col lg:gap-6">
        <div className="lg:max-w-xl">
          <SearchInput
            value={term}
            onValueChange={setTerm}
            label="Search sites and owners"
            placeholder="slug, site name, or email address"
            autoFocus
          />
          {trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH && (
            <Text size="caption" tone="subtle" className="lg:mt-2">
              Keep typing, at least two characters.
            </Text>
          )}
        </div>

        {results.error && (
          <Text size="sm" tone="danger" role="alert">
            {results.error.message}
          </Text>
        )}

        {results.data && (
          <>
            <section className="lg:flex lg:flex-col lg:gap-2">
              <H2 className="lg:text-h3">Sites</H2>
              <DataTable
                columns={SITE_COLUMNS}
                data={results.data.sites}
                caption="Sites matching the search"
                loading={results.isFetching}
                empty={<EmptyState icon={<SearchX />} title="No sites match" glow={false} />}
              />
              {/*
                The count, because the server caps the answer. Twenty-five rows
                for a common domain read as "these are the affected sites" when
                they were the first twenty-five of four hundred.
              */}
              <Text size="caption" tone="subtle">
                {formatCount(results.data.sites.length, results.data.siteTotal, "site")}
              </Text>
            </section>

            <section className="lg:flex lg:flex-col lg:gap-2">
              <H2 className="lg:text-h3">Owners</H2>
              <DataTable
                columns={OWNER_COLUMNS}
                data={results.data.owners}
                caption="Owners matching the search"
                loading={results.isFetching}
                empty={<EmptyState icon={<SearchX />} title="No owners match" glow={false} />}
              />
              <Text size="caption" tone="subtle">
                {formatCount(results.data.owners.length, results.data.ownerTotal, "owner")}
              </Text>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The five figures the header used to carry in one line of small print.
 *
 * Suspended sites and a non-empty screening queue are painted in the brand
 * pink, because both mean somebody has to do something; the other three are
 * the shape of the platform and are meant to be glanced at.
 */
function Counts({ health }: { health: Health | undefined }): React.JSX.Element | null {
  if (health === undefined) return null;

  return (
    <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:md:grid-cols-3 lg:xl:grid-cols-5">
      <Stat value={health.sites.toLocaleString()} label="Sites" />
      <Stat
        value={health.suspended.toLocaleString()}
        label="Suspended"
        emphasis={health.suspended > 0}
        {...(health.suspended > 0 ? { foot: "Not answering for anybody" } : {})}
      />
      <Stat
        value={health.screeningQueue.toLocaleString()}
        label="Waiting on screening"
        emphasis={health.screeningQueue > 0}
        foot={
          health.screeningQueue > 0 ? (
            <a href="#/screening" className="lg:text-primary lg:hover:underline">
              Review the queue
            </a>
          ) : (
            "Nothing waiting"
          )
        }
      />
      <Stat value={health.liveViewerSessions.toLocaleString()} label="Live viewer sessions" />
      <Stat
        value={health.auditEvents24h.toLocaleString()}
        label="Audit events"
        foot="Last 24 hours"
      />
    </div>
  );
}

const SITE_COLUMNS: DataTableColumn<SearchSite>[] = [
  {
    accessorKey: "slug",
    header: "Slug",
    cell: (info) => (
      <a
        href={`#/site/${info.row.original.id}`}
        className="lg:font-mono lg:text-primary lg:hover:underline"
      >
        {info.row.original.slug}
      </a>
    ),
  },
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "ownerEmail",
    header: "Owner",
    cell: (info) => (
      <Mono className="lg:text-muted-foreground">{info.row.original.ownerEmail}</Mono>
    ),
  },
  {
    id: "state",
    header: "State",
    cell: (info) => (
      <SiteStateBadge
        suspendedAt={info.row.original.suspendedAt}
        publishedDeployId={info.row.original.publishedDeployId}
      />
    ),
  },
];

const OWNER_COLUMNS: DataTableColumn<SearchOwner>[] = [
  {
    accessorKey: "email",
    header: "Email",
    // A link, as the slug beside it always was. Search was the only place an
    // owner appeared and none of the cells went anywhere, so suspending one
    // was a curl-only operation (LGN-125, audit M8).
    cell: (info) => (
      <a
        href={`#/owner/${info.row.original.id}`}
        className="lg:font-mono lg:text-primary lg:hover:underline"
      >
        {info.row.original.email}
      </a>
    ),
  },
  {
    accessorKey: "name",
    header: "Name",
    cell: (info) => info.row.original.name ?? <span className="lg:text-subtle">not set</span>,
  },
  {
    accessorKey: "siteCount",
    header: "Sites",
    cell: (info) => <Mono>{info.row.original.siteCount.toLocaleString()}</Mono>,
  },
  {
    id: "state",
    header: "State",
    cell: (info) => <AccountStateBadge suspendedAt={info.row.original.suspendedAt} />,
  },
];
